import { Groupe, GroupeSelectInput } from "@repo/types";
import { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { toast } from "sonner";

import { Badge } from "../../components/ui/badge";
import { DataTable } from "../../components/ui/data-table";
import { ConfirmationDialog } from "../../components/ui/data-table/confirmation-dialog";
import DataTableSearch from "../../components/ui/data-table/data-table-search";
import { DataTableRowActions } from "../../components/ui/data-table/data-table-row-actions";
import { StatusBadge } from "../../components/ui/data-table/status-badge";
import { Label } from "../../components/ui/label";
import { ListSortSelect } from "../../components/ui/list-sort-select";
import { Switch } from "../../components/ui/switch";
import { useDeleteGroupe, useToggleStrippedStatus } from "../../hooks/api/groupe";

type StrippedFilter = NonNullable<GroupeSelectInput["is_stripped"]>;

const GROUPE_SORT_OPTIONS = [
  { value: "alphanum", label: "Nom" },
  { value: "recent", label: "Plus récents" },
  { value: "oldest", label: "Plus anciens" },
] as const;

interface GroupeListProps {
  groupes: Groupe[];
  isLoading: boolean;
  error: Error | null;
  canManage: boolean;
  onView: (id: number) => void;
  onEdit: (id: number) => void;
  page: number;
  limit: number;
  total: number;
  pageCount: number;
  searchTerm: string;
  sortBy: string;
  strippedFilter: StrippedFilter;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onSearchChange: (term: string) => void;
  onSortChange: (sort: string) => void;
  onStrippedFilterChange: (value: StrippedFilter) => void;
}

export function GroupeList({
  groupes,
  isLoading,
  error,
  canManage,
  onView,
  onEdit,
  page,
  limit,
  total,
  pageCount,
  searchTerm,
  sortBy,
  strippedFilter,
  onPageChange,
  onLimitChange,
  onSearchChange,
  onSortChange,
  onStrippedFilterChange,
}: GroupeListProps) {
  const deleteGroupe = useDeleteGroupe();
  const toggleStripped = useToggleStrippedStatus();
  const [groupeToDelete, setGroupeToDelete] = useState<Groupe | null>(null);

  const handleConfirmDelete = () => {
    if (!groupeToDelete) return;
    deleteGroupe.mutate(groupeToDelete.id, {
      onSuccess: () => setGroupeToDelete(null),
    });
  };

  const handleToggleStripped = (groupe: Groupe) => {
    toggleStripped.mutate(
      { id: groupe.id, isStripped: !groupe.is_stripped },
      {
        onSuccess: (response) => {
          const message =
            response.message ||
            (response.data.is_stripped
              ? "Mode limité activé"
              : "Mode limité désactivé");
          toast("Statut modifié", { description: message });
        },
        onError: () => {
          toast("Erreur", {
            description: "Impossible de changer le statut du groupe.",
          });
        },
      }
    );
  };

  const columns: ColumnDef<Groupe>[] = [
    {
      accessorKey: "name",
      header: "Nom",
      cell: ({ row }) => (
        <span className="font-medium">{row.original.name || "-"}</span>
      ),
    },
    {
      accessorKey: "groupe_type",
      header: "Accès",
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          {row.original.groupe_type || "-"}
        </span>
      ),
    },
    {
      accessorKey: "licence",
      header: "Licence",
      cell: ({ row }) => (
        <Badge variant="outline">{row.original.licence ?? "-"}</Badge>
      ),
    },
    {
      accessorKey: "forfait",
      header: "Forfait",
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          {row.original?.forfait?.name || "-"}
        </span>
      ),
    },
    {
      accessorKey: "date_abonnement",
      header: "Date d'abonnement",
      cell: ({ row }) => {
        const date = row.original.created_at;
        return date ? (
          <span className="text-muted-foreground">
            {new Date(date).toLocaleDateString()}
          </span>
        ) : (
          "-"
        );
      },
    },
    {
      accessorKey: "date_echeance",
      header: "Date d'échéance",
      cell: ({ row }) => {
        const date = row.original.date_echeance;
        return date ? (
          <span className="text-muted-foreground">
            {new Date(date).toLocaleDateString()}
          </span>
        ) : (
          "-"
        );
      },
    },
    {
      accessorKey: "status",
      header: "Statut",
      cell: ({ row }) =>
        row.original.is_init ? (
          <StatusBadge tone="active" label="Actif" />
        ) : (
          <StatusBadge tone="inactive" label="Inactif" />
        ),
    },
    {
      accessorKey: "is_stripped",
      header: "Mode",
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          {row.original.is_stripped ? (
            <StatusBadge tone="warning" label="Limité" />
          ) : (
            <StatusBadge tone="active" label="Complet" />
          )}
          {canManage && (
            <Switch
              checked={!!row.original.is_stripped}
              onCheckedChange={() => handleToggleStripped(row.original)}
              aria-label={
                row.original.is_stripped
                  ? "Activer le mode complet"
                  : "Activer le mode limité"
              }
            />
          )}
        </div>
      ),
    },
    {
      id: "actions",
      header: () => <div className="text-right pr-2">Actions</div>,
      cell: ({ row }) => (
        <DataTableRowActions
          onView={() => onView(row.original.id)}
          onEdit={canManage ? () => onEdit(row.original.id) : undefined}
          onDelete={canManage ? () => setGroupeToDelete(row.original) : undefined}
        />
      ),
    },
  ];

  if (error) return <div className="text-destructive">Erreur: {error.message}</div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px]">
          <DataTableSearch
            value={searchTerm}
            onChange={onSearchChange}
            placeholder="Rechercher par nom"
          />
        </div>

        <div className="flex items-center gap-2">
          <Label htmlFor="stripped-filter" className="text-sm text-muted-foreground whitespace-nowrap">
            Afficher :
          </Label>
          <select
            id="stripped-filter"
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            value={strippedFilter}
            onChange={(event) =>
              onStrippedFilterChange(event.target.value as StrippedFilter)
            }
          >
            <option value="all">Tous les groupes</option>
            <option value="stripped">Mode limité</option>
            <option value="non-stripped">Mode complet</option>
          </select>
        </div>

        <ListSortSelect
          id="groupe-sort"
          value={sortBy}
          options={[...GROUPE_SORT_OPTIONS]}
          onValueChange={onSortChange}
        />
      </div>

      <div className="rounded-md shadow-sm bg-background">
        <DataTable
          serverSidePagination
          columns={columns}
          data={groupes}
          isLoading={isLoading}
          renderSearchOutside
          externalGlobalFilter={searchTerm}
          onGlobalFilterChange={onSearchChange}
          pageCount={pageCount}
          pageSize={limit}
          totalCount={total}
          onPageChange={onPageChange}
          onPageSizeChange={onLimitChange}
          searchPlaceholder="Rechercher un groupe..."
          currentPage={page}
        />
      </div>

      <ConfirmationDialog
        open={!!groupeToDelete}
        title="Supprimer le groupe ?"
        description={`Cette action ne peut pas être annulée. Le groupe « ${groupeToDelete?.name ?? ""} » sera supprimé définitivement.`}
        confirmLabel="Supprimer"
        variant="destructive"
        isLoading={deleteGroupe.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setGroupeToDelete(null)}
      />
    </div>
  );
}
